import React from "react";
import { Link } from "react-router-dom";
import { useWorkspace, timeLabel } from "../workspace/useWorkspace";
import {
  ReviewList,
  GoalCard,
  AttentionList,
  RuntimeNote,
} from "../workspace/WorkspaceCards";
export default function TodayPage() {
  const { data, isPending, error, refetch } = useWorkspace();
  if (isPending)
    return (
      <main className="unified-page" role="status">
        Gathering your day…
      </main>
    );
  if (error)
    return (
      <main className="unified-page">
        <p role="alert">Your briefing couldn’t load.</p>
        <button className="secondary-button" onClick={() => refetch()}>
          Try again
        </button>
      </main>
    );
  const {
    reviews = [],
    attention = [],
    events = [],
    goals = [],
    learning = {},
    timezone,
  } = data;
  return (
    <main className="unified-page">
      <header className="unified-page-header">
        <div>
          <p className="workspace-eyebrow">
            Today ·{" "}
            {new Intl.DateTimeFormat(undefined, {
              timeZone: timezone,
              weekday: "long",
              month: "short",
              day: "numeric",
            }).format(new Date())}
          </p>
          <h1>A little less to keep in mind.</h1>
          <p>
            {reviews.length
              ? `${reviews.length} decision${reviews.length === 1 ? "" : "s"} ready for you.`
              : "Your day, your commitments and the next useful step."}
          </p>
        </div>
        <Link className="primary-button" to="/ai">
          Talk to your assistant
        </Link>
      </header>
      <RuntimeNote data={data} />
      <div className="briefing-layout">
        <div>
          <section className="briefing-section">
            <div className="section-title">
              <h2>Needs you</h2>
              <span>{reviews.length}</span>
            </div>
            <ReviewList items={reviews} />
          </section>
          {attention.length > 0 && (
            <section className="briefing-section">
              <h2>Worth your attention</h2>
              <AttentionList
                items={[...attention].sort(
                  (a, b) =>
                    (a.priority === "critical" ? -1 : 0) -
                    (b.priority === "critical" ? -1 : 0),
                )}
                timezone={timezone}
              />
            </section>
          )}
          <section className="briefing-section">
            <div className="section-title">
              <h2>Keep moving forward</h2>
              <Link to="/goals">All goals</Link>
            </div>
            <div className="goal-grid">
              {goals.slice(0, 4).map((goal) => (
                <GoalCard
                  key={`${goal.kind}:${goal.id}`}
                  goal={goal}
                  timezone={timezone}
                />
              ))}
            </div>
            {!goals.length && (
              <div className="workspace-empty">
                <p>Give your assistant one thing to help you stay on top of.</p>
                <Link
                  className="workspace-action"
                  to="/ai"
                  state={{
                    prompt:
                      "Help me set up a goal and a practical check-in routine. Ask me what I want to achieve first.",
                  }}
                >
                  Start with a goal
                </Link>
              </div>
            )}
          </section>
        </div>
        <aside>
          <section className="briefing-section">
            <div className="section-title">
              <h2>Coming up</h2>
              <Link to="/calendar">Calendar</Link>
            </div>
            <p className="workspace-caption">Times in {timezone}</p>
            {events.slice(0, 5).map((event) => (
              <Link className="schedule-row" key={event.id} to={event.link}>
                <time>
                  {event.all_day
                    ? "All day"
                    : timeLabel(event.start_time, timezone)}
                </time>
                <strong>{event.title}</strong>
                <span>Open meeting preparation ↗</span>
              </Link>
            ))}
            {!events.length && (
              <p className="workspace-empty">
                No saved commitments in the next seven days.
              </p>
            )}
          </section>
          <section className="briefing-section">
            <h2>Your learning</h2>
            <p className="learning-total">
              {learning.weekly_minutes || 0}
              <span> minutes this week</span>
            </p>
            {learning.up_next?.slice(0, 3).map((item) => (
              <Link className="schedule-row" to="/learning" key={item.id}>
                <strong>{item.title}</strong>
                <span>Next in your roadmap</span>
              </Link>
            ))}
            <Link className="workspace-text-link" to="/learning">
              Open learning journal
            </Link>
          </section>
          <section className="briefing-section">
            <h2>Working quietly</h2>
            {data.watches
              ?.filter((w) => w.enabled)
              .slice(0, 4)
              .map((w) => (
                <Link key={w.id} to={w.link} className="schedule-row">
                  <strong>{w.title}</strong>
                  <span>Next check {timeLabel(w.next_at, timezone)}</span>
                </Link>
              ))}
            {!data.watches?.some((w) => w.enabled) && (
              <p className="workspace-empty">
                Create a recurring research check when you have something you
                want followed over time.
              </p>
            )}
            <Link className="workspace-text-link" to="/manage?view=agent">
              Manage schedules
            </Link>
          </section>
        </aside>
      </div>
    </main>
  );
}
